import { useState } from "react";
import { Button } from "@shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@shared/components/ui/dialog";
import CityForm from "./CityForm"; // Import the form component
import { Pen, Plus, Trash2 } from "lucide-react";
import { useCreateCity, useUpdateCity, useDeleteCity } from "./useCities";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useCityValidation } from "./useCityValidation";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@shared/components/ui/alert-dialog";

export default function CityActions({
  initialData = {},
  mode = "create",
  onClose,
}) {
  const { t } = useTranslation();
  const [city, setCity] = useState(initialData);
  const [isOpen, setIsOpen] = useState(false);
  const { mutate: createCity, isPending: isCreating } = useCreateCity();
  const { mutate: updateCity, isPending: isUpdating } = useUpdateCity();
  const { mutate: deleteCity, isPending: isDeleting } = useDeleteCity();

  // Use Zod validation hook instead of manual validation
  const { validateForm, errors, clearErrors } = useCityValidation();

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validate using Zod schema
    const validationResult = validateForm(city);
    if (!validationResult.isValid) {
      toast.error(t("validation.pleaseFixErrors"));
      return;
    }

    // Use validated data from Zod for API calls
    const validatedCity = validationResult.data;

    if (mode === "create") {
      createCity(validatedCity, {
        onSuccess: () => {
          toast.success(t("messages.cityCreated"));
          resetForm();
        },
        onError: (error) => {
          toast.error(error.message || t("messages.cityCreationFailed"));
        },
      });
    } else if (mode === "update") {
      updateCity(
        {
          id: initialData._id,
          city: validatedCity,
        },
        {
          onSuccess: () => {
            toast.success(t("messages.cityUpdated"));
            setIsOpen(false);
          },
          onError: (error) => {
            toast.error(error.message || t("messages.cityUpdateFailed"));
          },
        }
      );
    }
  };

  const handleDelete = () => {
    deleteCity(initialData._id, {
      onSuccess: () => {
        toast.success(t("messages.cityDeleted"));
      },
      onError: (error) => {
        toast.error(error.message || t("messages.cityDeletionFailed"));
      },
    });
  };

  const handleChange = (e) => {
    const { id, value } = e.target;
    setCity({ ...city, [id]: value });
  };

  const resetForm = () => {
    setCity({});
    clearErrors();
    setIsOpen(false);
    if (onClose) onClose();
  };

  // Delete confirmation dialog for individual cities
  if (mode === "delete") {
    return (
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button
            variant="outline"
            size="icon"
            className="text-red-600 hover:text-red-700"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("city.deleteConfirmTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("city.deleteConfirmMessage", { cityName: initialData.name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeleting ? t("common.deleting") : t("common.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    );
  }

  return (
    <>
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogTrigger asChild>
          <Button
            variant={mode === "update" ? "outline" : "default"}
            size={mode === "update" ? "icon" : "default"}
            className="cursor-pointer"
          >
            {mode === "create" ? (
              <Plus className="mr-1" />
            ) : (
              <Pen className="size-4" />
            )}

            {mode === "create" ? t("city.add") : ""}
          </Button>
        </DialogTrigger>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <form onSubmit={handleSubmit}>
            <DialogHeader>
              <DialogTitle>
                {mode === "create" ? t("city.add") : t("city.update")}
              </DialogTitle>
              <DialogDescription>
                {mode === "create"
                  ? t("city.createDescription")
                  : t("city.updateDescription")}
              </DialogDescription>
            </DialogHeader>

            {/* Use the CityForm component with Zod validation */}
            <div className="py-4">
              <CityForm
                city={city}
                handleChange={handleChange}
                setCity={setCity}
                errors={errors}
                setErrors={() => {}} // Not needed as CityForm handles its own validation
              />
            </div>

            <DialogFooter className="flex justify-between gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={resetForm}
                disabled={isCreating || isUpdating}
              >
                {t("common.cancel")}
              </Button>
              <Button
                type="submit"
                disabled={
                  isCreating || isUpdating || Object.keys(errors).length > 0
                }
              >
                {isCreating || isUpdating
                  ? t("common.saving")
                  : mode === "create"
                  ? t("city.create")
                  : t("city.update")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
